// Marketing case-study content for /how-it-works.
//
// Per docs/plans/10-HowItWorks-Page-Content.md:
//   - generic patient identifiers (Patient 1 / 2 / 3)
//   - generic payer categories (no named insurers)
//   - outcome framing as published RANGES with a real source — never a
//     stated result for the illustrative patient
//   - "Illustrative example" badge + footer disclaimer enforced by the
//     CaseStudyCard renderer
//
// The excerpts below are placeholder approximations of what the system
// would produce. Per plan 10 §"Authoring the three case studies", the
// canonical version of these excerpts should come from running the
// real paid pipeline against committed sample denial PDFs in
// `samples/marketing/case_NN_*.pdf`. Replace these strings when those
// runs land.

export type CaseStudyKind = "appeal" | "refund";

export interface CaseStudyCitation {
  marker: string;
  label: string;
}

export interface CaseStudy {
  id: string;
  patientLabel: string;
  procedure: string;
  cptCode: string;
  payerCategory: string;
  probability: number; // 0..1
  denialSummary: string;
  kind: CaseStudyKind;
  /** For `kind === "appeal"`: ~200-word excerpt from the generated letter. */
  excerpt?: string;
  /** Citations referenced as `[1]`, `[2]` in the excerpt. */
  citations?: CaseStudyCitation[];
  /** Outcome framing — published range for the category, with a real source. */
  outcomeFraming: string;
  outcomeSource: string;
}

const CASE_1_EXCERPT = `Mr. [Patient 1] presents with subacute lumbar pain refractory to a documented six-week trial of conservative management, including NSAIDs and structured physical therapy as recorded in the primary care progress note dated [date]. The American College of Radiology Appropriateness Criteria for low back pain explicitly designate advanced imaging as "Usually Appropriate" (rating 7) for patients whose symptoms persist beyond six weeks of conservative therapy [1]. CMS coverage guidance for advanced spinal imaging similarly establishes medical necessity once a documented conservative trial has failed and the clinical examination supports a structural etiology [2]. The criteria the insurer cites — "duration of symptoms inadequate to justify advanced imaging" — does not match the documented timeline; the medical record establishes more than six weeks of symptoms with conservative-management failure. The requested imaging is therefore medically necessary under the carrier's own published policy and the established standard of care.`;

const CASE_2_EXCERPT = `The prudent-layperson standard codified in 42 U.S.C. § 1395dd (EMTALA) and adopted by the ACA requires that emergency-services coverage be evaluated based on the symptoms presenting at the time of care, not on the final diagnosis [1]. Mr. [Patient 2] presented with acute substernal chest pain, dyspnea, and diaphoresis — a constellation of symptoms a prudent layperson with average knowledge of health and medicine would reasonably believe could indicate a serious medical condition requiring immediate care, specifically acute coronary syndrome. The treating physician's documented workup — 12-lead ECG, serial troponin testing, and observation — reflects the standard-of-care evaluation for chest pain of suspected cardiac origin [2]. That subsequent evaluation excluded ACS does not retroactively reclassify the visit as non-emergent. The CPT 99284 level was supported by the moderate-complexity decision-making documented in the encounter note. The insurer's retrospective downgrade misapplies the prudent-layperson standard and should be reversed.`;

export const CASE_STUDIES: CaseStudy[] = [
  {
    id: "case-1-mri",
    patientLabel: "Patient 1",
    procedure: "Lumbar spine MRI without contrast",
    cptCode: "CPT 72148",
    payerCategory: "A major national PPO insurer",
    probability: 0.78,
    denialSummary:
      "The insurer said the patient's back pain hasn't been documented long enough to justify imaging.",
    kind: "appeal",
    excerpt: CASE_1_EXCERPT,
    citations: [
      {
        marker: "[1]",
        label:
          "ACR Appropriateness Criteria — Low Back Pain (American College of Radiology)",
      },
      {
        marker: "[2]",
        label: "CMS National Coverage Determination — advanced spinal imaging",
      },
    ],
    outcomeFraming:
      "In published statistics, MRI denials on medical-necessity grounds are overturned in roughly 60–75% of appeals when supported by clinical documentation.",
    outcomeSource: "KFF analysis of CMS appeals data",
  },
  {
    id: "case-2-ed",
    patientLabel: "Patient 2",
    procedure: "Emergency department visit, level 4",
    cptCode: "CPT 99284",
    payerCategory: "A national HMO insurer",
    probability: 0.64,
    denialSummary:
      "The insurer decided the chest pain the patient went to the ER for didn't qualify as an emergency after the fact.",
    kind: "appeal",
    excerpt: CASE_2_EXCERPT,
    citations: [
      { marker: "[1]", label: "EMTALA / 42 U.S.C. § 1395dd, prudent-layperson standard" },
      {
        marker: "[2]",
        label: "ACEP Clinical Policy — Evaluation of Adult Patients with Acute Chest Pain",
      },
    ],
    outcomeFraming:
      "Retrospective ED downgrade appeals citing the prudent-layperson standard are upheld at meaningful rates when presenting symptoms could plausibly have indicated a serious condition.",
    outcomeSource: "Public ACEP / state-DOI reviews of ED downgrade appeals",
  },
  {
    id: "case-3-cosmetic",
    patientLabel: "Patient 3",
    procedure: "Blepharoplasty",
    cptCode: "CPT 15822",
    payerCategory: "A national managed-care insurer",
    probability: 0.22,
    denialSummary:
      "The insurer classified the procedure as cosmetic; the patient's records don't document a functional vision impairment.",
    kind: "refund",
    outcomeFraming:
      "Cosmetic-classification denials without documented functional impairment have low success rates on appeal. In this scenario, our recommendation would be a refund and a consult with a prior-authorization specialist before re-submitting.",
    outcomeSource: "CMS appeals statistics + payer policy bulletins for blepharoplasty coverage",
  },
];
